import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FieldErrors } from 'react-hook-form';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { marksReportService } from '@/api/domains/attendance/services/marksReport.service';
import type { MarkReport } from '@/api/domains/attendance/types/marksReport.types';
import {
  adjustMarkFormSchema,
  type AdjustMarkFormValues,
} from '@/schemas/attendance/adjustMarkForm';


interface UseAdjustMarkFormParams {
  record?: MarkReport | null;
  onSubmit?: () => void;
  readOnly?: boolean;
}

export interface UseAdjustMarkFormResult {
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
  register: ReturnType<typeof useForm<AdjustMarkFormValues>>['register'];
  errors: FieldErrors<AdjustMarkFormValues>;
  isSubmitted: boolean;
  newTime: string;
  newEventId: string;
  comment: string;
  handleTimeChange: (value: string) => void;
  handleTimePickerOpenChange: (isOpen: boolean) => void;
  handleEventChange: (value: string) => void;
  handleCommentChange: (value: string) => void;
}

export function useAdjustMarkForm({
  record,
  onSubmit: onSubmitCallback,
  readOnly,
}: UseAdjustMarkFormParams = {}): UseAdjustMarkFormResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showApiError, closeSidePanel } = useUI();
  const effectiveReadOnly = !!readOnly;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [newEventId, setNewEventId] = useState('');
  const [comment, setComment] = useState('');

  const {
    handleSubmit: rhfHandleSubmit,
    register,
    setError,
    setValue,
    formState,
  } = useForm<AdjustMarkFormValues>({
    resolver: zodResolver(adjustMarkFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      newTime: '',
      newEventId: '',
      comment: '',
    },
  });

  // TimePicker emite un valor por defecto ('00:00:00') al montar, antes de que el usuario
  // interactúe (ver src/components/ui/inputs/TimePicker.tsx). Se ignora ese valor hasta que
  // el usuario abra el selector al menos una vez, para poder mantener "sin cambio" (null) real.
  const hasOpenedTimePickerRef = useRef(false);

  const handleTimePickerOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) hasOpenedTimePickerRef.current = true;
  }, []);

  const handleTimeChange = useCallback((value: string) => {
    if (!hasOpenedTimePickerRef.current) return;
    setNewTime(value);
    setValue('newTime', value, { shouldDirty: true });
  }, [setValue]);

  const handleEventChange = useCallback((value: string) => {
    setNewEventId(value);
    setValue('newEventId', value, { shouldDirty: true });
  }, [setValue]);

  const handleCommentChange = useCallback((value: string) => {
    setComment(value);
    setValue('comment', value, { shouldDirty: true });
  }, [setValue]);

  const isReady = useMemo(() => {
    const hasTimeOrEvent = newTime.length > 0 || newEventId.length > 0;
    const hasComment = comment.trim().length > 0;
    return hasTimeOrEvent && hasComment;
  }, [newTime, newEventId, comment]);

  const submitAdjustMark = useCallback(async (data: AdjustMarkFormValues) => {
    if (effectiveReadOnly || !record) return;
    setIsSubmitting(true);

    try {
      const response = await marksReportService.adjustMark(record.id, {
        newTime: data.newTime ?? null,
        newEventId: data.newEventId ? Number(data.newEventId) : null,
        comment: data.comment,
      });

      if (response.success && response.data) {
        showSuccess(
          response.data.isPendingApproval
            ? translate('attendance:marksReport.adjust.messages.successPending')
            : translate('attendance:marksReport.adjust.messages.successDirect'),
        );
        onSubmitCallback?.();
        closeSidePanel?.();
      } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
        injectValidationErrors(response.error.validationErrors, { setError });
      } else {
        showApiError(response, translate('attendance:marksReport.adjust.messages.error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    effectiveReadOnly, record, showSuccess, showApiError, onSubmitCallback,
    closeSidePanel, translate, setError,
  ]);

  const onValid = useCallback(async (data: AdjustMarkFormValues) => {
    await submitAdjustMark(data);
  }, [submitAdjustMark]);

  const onInvalid = useCallback((): void => {}, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      if (effectiveReadOnly || isSubmitting) return;
      const wrappedValid = (data: AdjustMarkFormValues) => { void onValid(data); };
      const wrappedInvalid = () => { onInvalid(); };
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid, onInvalid, effectiveReadOnly, isSubmitting],
  );

  return {
    effectiveReadOnly,
    isSubmitting,
    isReady,
    handleSubmit,
    register,
    errors: formState.errors,
    isSubmitted: formState.isSubmitted,
    newTime,
    newEventId,
    comment,
    handleTimeChange,
    handleTimePickerOpenChange,
    handleEventChange,
    handleCommentChange,
  };
}

export default useAdjustMarkForm;
